import { useContext, useEffect, useState } from 'react';

import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box } from '@material-ui/core';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { MAIN_CONTAINER_ID } from '../../constants';
import { DeviceContext } from '../../contexts';
import { ButtonTitleWithLoading } from '../ButtonTitleWithLoading';
import { AppTextField } from '../inputs/AppTextField';
import { ChatUser } from '../../types';
import { ChatSearchUsersAutocomplete } from './ChatSearchUsersAutocomplete';

interface Form {
  chatName: string;
  users: ChatUser[];
}

export const ChatNewGroupChatDialog = ({
  open,
  handleClose,
  createGroupChat,
  searchChatUsers,
}: {
  open: boolean;
  handleClose: () => void;
  createGroupChat: (data: Form) => Promise<void>;
  searchChatUsers: (searchQuery: string) => Promise<ChatUser[]>;
}) => {
  const { isMobile } = useContext(DeviceContext);
  const methods = useForm<Form>({
    defaultValues: {
      chatName: '',
      users: [],
    },
  });
  const { control, watch } = methods;
  const [searchItems, setSearchItems] = useState<ChatUser[]>([]);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (data: Form) => {
    setLoading(true);
    await createGroupChat(data);
    setLoading(false);
    handleClose();
  };

  const users = watch('users');
  const chatName = watch('chatName');

  const onSearch = async (search: string) => {
    try {
      const users = await searchChatUsers(search);
      setSearchItems(users);
    } catch (e) {
      console.error(e);
      methods.setValue('users', []);
    }
  };

  useEffect(() => {
    if (open) {
      onSearch('');
    }
  }, [open]);

  return (
    <Dialog
      container={document.getElementById(MAIN_CONTAINER_ID)}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      open={open}
      onClose={handleClose}
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleSubmit)}>
          <DialogTitle>New group chat</DialogTitle>

          <DialogContent>
            <Box display="flex" flexDirection="column" gridGap={24}>
              <Controller
                control={control}
                name="chatName"
                render={({ field }) => (
                  <AppTextField
                    label="Chat name"
                    value={field.value}
                    onChange={e => field.onChange(e.target.value)}
                    fullWidth
                  />
                )}
              />

              <ChatSearchUsersAutocomplete
                selectedItems={users}
                users={searchItems}
                onSearch={onSearch}
                label="Select chat members"
              />
            </Box>
          </DialogContent>

          <DialogActions>
            <Button variant="outlined" onClick={handleClose}>
              Cancel
            </Button>

            <Button
              style={{ width: 130 }}
              disabled={!chatName || !users.length || loading}
              onClick={methods.handleSubmit(handleSubmit)}
              color="primary"
              variant="outlined"
            >
              <ButtonTitleWithLoading title="Create Chat" loaderVariant="primary" loading={loading} />
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};
